import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Leaf } from "lucide-react";
import { BRAND, IMAGES } from "../data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Hero({ onQuote }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const prodY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  const headline = ["Sustainable Bagasse", "Tableware for a", "Greener Future"];

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative min-h-screen bg-verra-blue overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={IMAGES.heroField} alt="Sugarcane field" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(8,32,15,0.93) 0%, rgba(18,58,28,0.85) 45%, rgba(27,94,32,0.55) 100%)" }} />
      </motion.div>
      <div className="absolute inset-0 grain-overlay pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-36 pb-20 min-h-screen grid lg:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y: contentY }} className="lg:col-span-8">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="mb-8">
            <span className="inline-flex items-center gap-2 rounded-full border border-verra-light/50 bg-black/10 backdrop-blur-sm px-4 py-1.5 text-verra-light text-xs uppercase tracking-[0.2em]">
              <Leaf className="w-3.5 h-3.5" /> {BRAND.name} · by {BRAND.parent}
            </span>
          </motion.div>

          <h1 aria-label="Sustainable Bagasse Tableware for a Greener Future" className="font-display text-white text-5xl md:text-6xl lg:text-[4.25rem] leading-[1.06] tracking-tight">
            {headline.map((l, i) => (
              <span key={i} className="block overflow-hidden" aria-hidden="true">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-xl text-white/80 text-lg leading-relaxed">
            Premium biodegradable food packaging made from renewable sugarcane bagasse. Designed for food businesses, distributors, retailers and global importers.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-quote-btn" onClick={onQuote}
              className="group inline-flex items-center gap-2 rounded-full bg-verra-green text-white font-semibold px-8 py-4 hover:bg-white hover:text-verra-blue transition-colors duration-300">
              Request a Quote
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <a href="#products" data-testid="hero-products-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white font-semibold px-8 py-4 hover:bg-white/10 transition-colors duration-300">
              View Products
            </a>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: prodY }} className="hidden lg:block lg:col-span-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-6 rounded-[2.5rem] bg-verra-light/20 blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden border border-white/15 shadow-2xl">
              <img src={IMAGES.showcase} alt="Verraleaf bagasse tableware range" className="w-full h-[520px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl px-6 py-4 shadow-xl">
              <p className="font-display text-2xl text-verra-green leading-none">100%</p>
              <p className="text-[11px] uppercase tracking-widest text-verra-blue/70 mt-1">Compostable & Biodegradable</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
